import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import { GoldButton, GhostButton, StatBadge } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, X, Pencil, Star, MapPin, Phone, Trash2, Car } from "lucide-react";

const LANGS = ["PT", "EN", "ES", "FR", "DE", "IT", "ZH"];
const DAY_LABELS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const PRICING_METHODS = [
  { v: "higher", l: "Maior (km ou hora)" },
  { v: "sum", l: "Soma km + hora" },
  { v: "km_only", l: "Apenas km" },
  { v: "hour_only", l: "Apenas hora" },
];

export default function FleetDrivers() {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [zones, setZones] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => Promise.all([
    api.get("/fleet/drivers"),
    api.get("/fleet/vehicles"),
    api.get("/public/zones"),
  ]).then(([d, v, z]) => {
    setDrivers(d.data);
    setVehicles(v.data);
    setZones(z.data);
  });
  useEffect(() => { load(); }, []);

  const remove = async (d) => {
    if (!window.confirm(`Remover ${d.name} da sua frota? O histórico permanece.`)) return;
    try { await api.delete(`/fleet/drivers/${d.id}`); toast.success("Motorista removido"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8 max-w-[1600px]" data-testid="fleet-drivers-page">
      <PortalPageHeader
        eyebrow="Equipa"
        title="Motoristas"
        description="Gerencie os motoristas da sua frota. O Admin DiTours aprova cada novo motorista antes de este começar a receber viagens."
      >
        <GoldButton onClick={() => { setEditing(null); setShow(true); }} data-testid="add-fleet-driver">
          <Plus className="w-4 h-4" /> Adicionar motorista
        </GoldButton>
      </PortalPageHeader>

      <div className="bg-white border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="border-b border-slate-200 text-left label-caps">
              <th className="p-4">Nome</th>
              <th className="p-4">Contacto</th>
              <th className="p-4">Idiomas</th>
              <th className="p-4">Zonas</th>
              <th className="p-4">Veículo</th>
              <th className="p-4">Rating</th>
              <th className="p-4">Estado</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {drivers.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-slate-500">Sem motoristas ainda.</td></tr>
            )}
            {drivers.map((d) => {
              const pv = vehicles.find((v) => v.id === d.primary_vehicle_id);
              return (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`fleet-driver-row-${d.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {d.photo_url ? (
                        <img src={d.photo_url} alt="" className="w-9 h-9 object-cover border border-slate-200" />
                      ) : (
                        <div className="w-9 h-9 bg-white border border-slate-200 flex items-center justify-center text-[#f59e0b] font-display">{d.name?.[0] || "M"}</div>
                      )}
                      <div>
                        <div className="text-[#0a0f1e] font-semibold">{d.name}</div>
                        <div className="text-xs text-slate-500 font-mono">{d.id.slice(0, 8)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-xs text-slate-700">
                    <div>{d.email}</div>
                    <div className="text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {d.phone || "—"}</div>
                  </td>
                  <td className="p-4 text-xs text-slate-700">{(d.languages || []).join(" · ") || "—"}</td>
                  <td className="p-4 text-xs text-slate-700 flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" /> {(d.zones_covered || []).join(", ") || "—"}</td>
                  <td className="p-4 text-xs">
                    {pv ? (
                      <div>
                        <div className="text-[#0a0f1e] font-semibold">{pv.brand} {pv.model}</div>
                        <div className="text-slate-500 font-mono">{pv.plate}</div>
                      </div>
                    ) : (
                      <span className="text-slate-400">Sem veículo</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-[#f59e0b] fill-[#f59e0b]" />
                      <span className="text-[#0a0f1e] text-xs font-bold">{d.rating_avg?.toFixed(1) || "—"}</span>
                    </div>
                  </td>
                  <td className="p-4"><StatBadge status={d.status} /></td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button onClick={() => { setEditing(d); setShow(true); }} className="text-slate-500 hover:text-[#f59e0b]" data-testid={`edit-fleet-driver-${d.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(d)} className="text-slate-500 hover:text-red-500" data-testid={`del-fleet-driver-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {show && (
        <DriverModal
          driver={editing}
          vehicles={vehicles}
          zones={zones}
          onClose={() => { setShow(false); setEditing(null); }}
          onSaved={() => { setShow(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function DriverModal({ driver, vehicles, zones, onClose, onSaved }) {
  const isEdit = !!driver;
  const emptyHours = Array.from({ length: 7 }, (_, i) => ({ day: i, enabled: true, start: "08:00", end: "22:00" }));

  const [f, setF] = useState(() => driver ? {
    name: driver.name || "",
    email: driver.email || "",
    phone: driver.phone || "",
    password: "",
    nif: driver.nif || "",
    photo_url: driver.photo_url || "",
    bio_pt: driver.bio_pt || "",
    bio_en: driver.bio_en || "",
    languages: driver.languages || ["PT", "EN"],
    zones_covered: driver.zones_covered || [],
    primary_vehicle_id: driver.primary_vehicle_id || "",
    pricing_km: driver.pricing_km ?? 1.5,
    pricing_hour: driver.pricing_hour ?? 45.0,
    pricing_method: driver.pricing_method || "higher",
    night_supplement_pct: driver.night_supplement_pct ?? 20.0,
    weekend_supplement_pct: driver.weekend_supplement_pct ?? 15.0,
    working_hours: driver.working_hours || emptyHours,
    max_distance_km: driver.max_distance_km ?? 200,
    min_advance_hours: driver.min_advance_hours ?? 2,
    min_gap_minutes: driver.min_gap_minutes ?? 15,
  } : {
    name: "", email: "", phone: "", password: "",
    nif: "", photo_url: "", bio_pt: "", bio_en: "",
    languages: ["PT", "EN"], zones_covered: ["lisboa"], primary_vehicle_id: "",
    pricing_km: 1.5, pricing_hour: 45.0, pricing_method: "higher",
    night_supplement_pct: 20.0, weekend_supplement_pct: 15.0,
    working_hours: emptyHours,
    max_distance_km: 200, min_advance_hours: 2, min_gap_minutes: 15,
  });
  const [saving, setSaving] = useState(false);

  const toggleLang = (l) => setF({ ...f, languages: f.languages.includes(l) ? f.languages.filter(x => x !== l) : [...f.languages, l] });
  const toggleZone = (k) => setF({ ...f, zones_covered: f.zones_covered.includes(k) ? f.zones_covered.filter(x => x !== k) : [...f.zones_covered, k] });
  const updHour = (i, key, val) => setF({ ...f, working_hours: f.working_hours.map((h, idx) => idx === i ? { ...h, [key]: val } : h) });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        // PATCH — exclude email/password (immutable) and any status field
        const { email: _e, password: _p, ...patch } = f;
        await api.patch(`/fleet/drivers/${driver.id}`, patch);
        toast.success("Motorista actualizado");
      } else {
        // POST — create needs email + password + minimum fields; extras persist via PATCH
        const created = await api.post("/fleet/drivers", { name: f.name, email: f.email, phone: f.phone, password: f.password });
        // Then push the full profile
        const { email: _e, password: _p, ...patch } = f;
        await api.patch(`/fleet/drivers/${created.data.id}`, patch);
        toast.success("Motorista criado. Admin irá aprovar em breve.");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="fleet-driver-modal">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-2xl text-[#0a0f1e]">{isEdit ? `Editar motorista · ${driver.name}` : "Novo motorista"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6 space-y-6">
          {/* Identity */}
          <Section title="Identificação">
            <div className="grid grid-cols-2 gap-3">
              <F l="Nome"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="fd-name" /></F>
              <F l="Email"><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} disabled={isEdit} data-testid="fd-email" /></F>
              <F l="Telefone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="fd-phone" /></F>
              {!isEdit && <F l="Password inicial"><Input required type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="fd-password" /></F>}
              <F l="NIF"><Input value={f.nif} onChange={(e) => setF({ ...f, nif: e.target.value })} data-testid="fd-nif" /></F>
              <F l="URL foto"><Input value={f.photo_url} onChange={(e) => setF({ ...f, photo_url: e.target.value })} data-testid="fd-photo" placeholder="https://..." /></F>
            </div>
          </Section>

          {/* Languages */}
          <Section title="Idiomas">
            <div className="flex flex-wrap gap-2">
              {LANGS.map((l) => (
                <button key={l} type="button" onClick={() => toggleLang(l)} data-testid={`fd-lang-${l}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wide border font-bold ${f.languages.includes(l) ? "border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200 text-slate-500"}`}>
                  {l}
                </button>
              ))}
            </div>
          </Section>

          {/* Zones */}
          <Section title="Zonas de trabalho">
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {zones.map((z) => (
                <button key={z.key} type="button" onClick={() => toggleZone(z.key)} data-testid={`fd-zone-${z.key}`}
                  className={`px-3 py-2 text-xs uppercase tracking-wide border font-bold text-left ${f.zones_covered.includes(z.key) ? "border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200 text-slate-500"}`}>
                  {z.label}
                </button>
              ))}
            </div>
          </Section>

          {/* Vehicle */}
          <Section title="Veículo primário">
            {vehicles.length === 0 ? (
              <div className="text-xs text-slate-500 italic">Registe primeiro veículos na sua frota.</div>
            ) : (
              <select value={f.primary_vehicle_id} onChange={(e) => setF({ ...f, primary_vehicle_id: e.target.value })} className="input-dt w-full" data-testid="fd-vehicle">
                <option value="">— Sem veículo atribuído —</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.brand} {v.model} · {v.plate}</option>)}
              </select>
            )}
          </Section>

          {/* Pricing */}
          <Section title="Preços">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <F l="€ / km"><Input type="number" step="0.1" value={f.pricing_km} onChange={(e) => setF({ ...f, pricing_km: Number(e.target.value) || 0 })} data-testid="fd-pkm" /></F>
              <F l="€ / hora"><Input type="number" step="0.5" value={f.pricing_hour} onChange={(e) => setF({ ...f, pricing_hour: Number(e.target.value) || 0 })} data-testid="fd-phour" /></F>
              <F l="Método">
                <select value={f.pricing_method} onChange={(e) => setF({ ...f, pricing_method: e.target.value })} className="input-dt w-full" data-testid="fd-method">
                  {PRICING_METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              </F>
              <F l="Suplemento noturno %"><Input type="number" value={f.night_supplement_pct} onChange={(e) => setF({ ...f, night_supplement_pct: Number(e.target.value) || 0 })} data-testid="fd-night" /></F>
              <F l="Suplemento fim-de-semana %"><Input type="number" value={f.weekend_supplement_pct} onChange={(e) => setF({ ...f, weekend_supplement_pct: Number(e.target.value) || 0 })} data-testid="fd-weekend" /></F>
              <F l="Distância máx (km)"><Input type="number" value={f.max_distance_km} onChange={(e) => setF({ ...f, max_distance_km: Number(e.target.value) || 0 })} data-testid="fd-maxkm" /></F>
            </div>
          </Section>

          {/* Availability */}
          <Section title="Horário base">
            <div className="grid grid-cols-1 gap-2">
              {f.working_hours.map((h, i) => (
                <div key={i} className="border border-slate-200 p-2 flex items-center gap-3" data-testid={`fd-day-${i}`}>
                  <label className="flex items-center gap-2 min-w-[60px]">
                    <input type="checkbox" checked={h.enabled} onChange={(e) => updHour(i, "enabled", e.target.checked)} />
                    <span className="text-xs font-bold">{DAY_LABELS[i]}</span>
                  </label>
                  <Input type="time" value={h.start} onChange={(e) => updHour(i, "start", e.target.value)} className="w-32 h-8" disabled={!h.enabled} />
                  <span className="text-slate-400">→</span>
                  <Input type="time" value={h.end} onChange={(e) => updHour(i, "end", e.target.value)} className="w-32 h-8" disabled={!h.enabled} />
                </div>
              ))}
            </div>
          </Section>

          {/* Bio */}
          <Section title="Biografia">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F l="Português">
                <Textarea rows={3} value={f.bio_pt} onChange={(e) => setF({ ...f, bio_pt: e.target.value })} data-testid="fd-bio-pt" />
              </F>
              <F l="English">
                <Textarea rows={3} value={f.bio_en} onChange={(e) => setF({ ...f, bio_en: e.target.value })} data-testid="fd-bio-en" />
              </F>
            </div>
          </Section>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <GhostButton type="button" onClick={onClose} className="flex-1">Cancelar</GhostButton>
          <GoldButton type="submit" disabled={saving} className="flex-1 justify-center" data-testid="fd-save">
            {saving ? "A guardar…" : (isEdit ? "Guardar alterações" : "Criar motorista")}
          </GoldButton>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-slate-100 pt-5 first:border-t-0 first:pt-0">
      <p className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold mb-3">{title}</p>
      {children}
    </div>
  );
}
function F({ l, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">{l}</label>
      {children}
    </div>
  );
}
